// utils/AgeCalculator.js

export class AgeCalculator {
  // Calculate precise age in days, months, years from DOB
  static calculatePreciseAge(dob) {
    const birthDate = new Date(dob);
    const today = new Date();
    
    let years = today.getFullYear() - birthDate.getFullYear();
    let months = today.getMonth() - birthDate.getMonth();
    let days = today.getDate() - birthDate.getDate();

    // Adjust for negative days
    if (days < 0) {
      months--;
      // Get days in previous month
      const prevMonth = new Date(today.getFullYear(), today.getMonth(), 0);
      days += prevMonth.getDate();
    }

    // Adjust for negative months
    if (months < 0) {
      years--;
      months += 12;
    }

    const totalDays = Math.floor((today - birthDate) / (1000 * 60 * 60 * 24));

    return {
      years,
      months,
      days,
      totalDays,
      displayText: this.formatAgeDisplay(years, months, days),
      forReferenceRange: totalDays // Use total days for precise reference range matching
    };
  }

  // Parse age string to days for reference range calculation
  static parseAgeToDays(ageString) {
    if (!ageString) return 0;
    
    // If it's a date string, calculate age from DOB
    if (this.isValidDate(ageString)) {
      const preciseAge = this.calculatePreciseAge(ageString);
      return preciseAge.totalDays;
    }
    
    // If it's already an age description
    const age = ageString.toString().toLowerCase().trim();
    
    // Handle "X years Y months Z days" format
    const yearsMatch = age.match(/(\d+)\s*years?/);
    const monthsMatch = age.match(/(\d+)\s*months?/);
    const daysMatch = age.match(/(\d+)\s*days?/);
    
    if (yearsMatch || monthsMatch || daysMatch) {
      const years = yearsMatch ? parseInt(yearsMatch[1]) : 0;
      const months = monthsMatch ? parseInt(monthsMatch[1]) : 0;
      const days = daysMatch ? parseInt(daysMatch[1]) : 0;
      
      return (years * 365) + (months * 30.44) + days;
    }
    
    // Handle simple number (assume years)
    const simpleNumber = parseFloat(age);
    if (!isNaN(simpleNumber)) {
      return simpleNumber * 365;
    }
    
    return 0;
  }

  // Format age for display
  static formatAgeDisplay(years, months, days) {
    if (years > 0) {
      if (months > 0) {
        return `${years} years ${months} months`;
      }
      return `${years} years`;
    } else if (months > 0) {
      if (days > 0) {
        return `${months} months ${days} days`;
      }
      return `${months} months`;
    } else {
      return `${days} days`;
    }
  }

  // Check if string is a valid date
  static isValidDate(dateString) {
    const date = new Date(dateString);
    return date instanceof Date && !isNaN(date);
  }

  // Backward compatibility - your existing function
  static calculateAgeInYears(dob) {
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDifference = today.getMonth() - birthDate.getMonth();
    const dayDifference = today.getDate() - birthDate.getDate();
    
    if (monthDifference < 0 || (monthDifference === 0 && dayDifference < 0)) {
      age--;
    }
    return age;
  }
}

// Export your existing function for backward compatibility
export const calculateAge = AgeCalculator.calculateAgeInYears;